import { EntityInterface } from '@gfe/core/models/entities/entity-visualization-interface';

import { EntityDetailInput } from './entity-details.component';

/**
 * One side of the split detail pane. A uid is open in at most one group, and a group
 * never stays empty: the transitions below drop it, collapsing the split back to one pane.
 */
export interface DetailGroup {
  tabs: EntityDetailInput[];
  activeUid: string | null;
}

export const MAX_DETAIL_GROUPS = 2;

export function findGroupIndex(groups: DetailGroup[], uid: string): number {
  return groups.findIndex((g) => g.tabs.some((t) => t.entity.uid === uid));
}

export function activeDetailOf(group: DetailGroup): EntityDetailInput | null {
  return group.tabs.find((t) => t.entity.uid === group.activeUid) ?? null;
}

export function openDetail(
  groups: DetailGroup[],
  input: EntityDetailInput,
  targetGroup: number
): DetailGroup[] {
  if (findGroupIndex(groups, input.entity.uid) !== -1) {
    return activateDetail(groups, input.entity.uid);
  }
  if (groups.length === 0) {
    return [{ tabs: [input], activeUid: input.entity.uid }];
  }
  const target = Math.min(Math.max(targetGroup, 0), groups.length - 1);
  return groups.map((g, i) =>
    i === target ? { tabs: [...g.tabs, input], activeUid: input.entity.uid } : g
  );
}

export function activateDetail(groups: DetailGroup[], uid: string): DetailGroup[] {
  const index = findGroupIndex(groups, uid);
  if (index === -1) return groups;
  return groups.map((g, i) => (i === index ? { ...g, activeUid: uid } : g));
}

export function closeDetail(groups: DetailGroup[], uid: string): DetailGroup[] {
  const index = findGroupIndex(groups, uid);
  if (index === -1) return groups;
  return groups
    .map((g, i) => (i === index ? withoutTab(g, uid) : g))
    .filter((g) => g.tabs.length > 0);
}

export function closeGroup(groups: DetailGroup[], groupIndex: number): DetailGroup[] {
  return groups.filter((_, i) => i !== groupIndex);
}

/**
 * Moves a tab to `toGroup` at `index` (the end by default) and activates it there.
 * `toGroup === groups.length` opens a new group, which is how a tab gets split out.
 */
export function moveDetail(
  groups: DetailGroup[],
  uid: string,
  toGroup: number,
  index?: number
): DetailGroup[] {
  const from = findGroupIndex(groups, uid);
  if (
    from === -1 ||
    toGroup < 0 ||
    toGroup > Math.min(groups.length, MAX_DETAIL_GROUPS - 1)
  ) {
    return groups;
  }
  const tab = groups[from].tabs.find((t) => t.entity.uid === uid)!;
  const next = groups.map((g, i) => (i === from ? withoutTab(g, uid) : g));
  const target = next[toGroup] ?? { tabs: [], activeUid: null };
  const tabs = [...target.tabs];
  tabs.splice(index ?? tabs.length, 0, tab);
  next[toGroup] = { tabs, activeUid: uid };
  return next.filter((g) => g.tabs.length > 0);
}

export function replaceEntity(
  groups: DetailGroup[],
  uid: string,
  entity: EntityInterface<unknown>
): DetailGroup[] {
  return groups.map((g) => ({
    ...g,
    tabs: g.tabs.map((t) => (t.entity.uid === uid ? { ...t, entity } : t))
  }));
}

function withoutTab(group: DetailGroup, uid: string): DetailGroup {
  const tabs = group.tabs.filter((t) => t.entity.uid !== uid);
  const activeUid =
    group.activeUid === uid ? (tabs.at(-1)?.entity.uid ?? null) : group.activeUid;
  return { tabs, activeUid };
}
